import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import $ from 'jquery';

import Master from '../Master';
import Collapsible from '../Panels/Collapsible';
import IziToast from '../IziToast';
import Select from '../Select';
import ReceiptsTable from './ReceiptsTable';
import DatePicker from '../DatePicker';
import Button from '../Buttons';
import AlertMessage from '../Alert';
import AlertActions from '../../actions/alert';
import { DestroyTable, DateTimeUTC, SortByLatest } from '../../utils/Functions';

const Receipts = ({ user, dispatch, alert }) => {
  // token
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };

  //   refs
  const propertyRef = useRef(null);
  const tenantRef = useRef(null);

  // state
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [lowerDate, setLowerDate] = useState(firstDay);
  const [upperDate, setUpperDate] = useState(lastDay);
  const [propertyId, setPropertyId] = useState('');
  const [properties, setProperties] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [status, setStatus] = useState('');
  const [tenantsList, setTenantsList] = useState([]);
  const [receiptList, setReceiptList] = useState([]);

  useEffect(() => {
    dispatch(AlertActions.clear());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        let url = `/byuser?status=active&user_id=${user.id}`;
        if (user && user.type === 'admin') {
          url = '?take=1000&where={"status":"active"}&order={"name":"ASC"}';
        }
        const {
          data: { response },
        } = await axios.get(
          `${process.env.REACT_APP_API_URL}properties${url}`,
          requestConfig,
        );
        setProperties(response);
        loadUnits(response);
        $(propertyRef.current)
          .select2()
          .on('change', (event) => {
            setPropertyId(event.val);
          });
      } catch (_) {
        IziToast('Could not load your properties');
      }
    })();
  }, []);

  useEffect(() => {
    filterByProperty();
    setTenantId('');
  }, [propertyId]);

  useEffect(() => {
    if (tenantId) {
      DestroyTable('receipts_table');
      const transaction = receiptList.filter((item) => {
        if (item && item.tenant) {
          return item.tenant.id === tenantId;
        }
        return undefined;
      });
      setReceipts(transaction);
    } else if (propertyId) {
      filterByProperty();
    } else {
      setReceipts(receiptList);
    }
  }, [tenantId]);

  useEffect(() => {
    if (receipts.length > 0) {
      const sum = receipts.reduce((acc, curr) => {
        return parseFloat(acc) + parseFloat(curr.amount);
      }, 0);
      setTotal(sum);
    } else {
      setTotal(0);
    }
  }, [receipts]);

  // Re-run the Tenant + Property joins whenever the lookup tables
  // (`tenantsList` / `units` / `properties`) finally arrive AFTER a
  // search has already populated the table. Same race as the Invoices
  // page — the user can click Search before the async tenant fetch
  // completes, which leaves Property / Unit / Name columns blank.
  // Re-mapping in place fills them in once the data lands; rows that
  // were already joined are left untouched.
  useEffect(() => {
    if (receiptList.length === 0) return;
    if (
      tenantsList.length === 0
      && properties.length === 0
      && units.length === 0
    ) return;
    const remapped = receiptList.map((rec) => {
      const out = { ...rec };
      if (!out.tenant) out.tenant = tenantsList.find((t) => t.id === rec.tenant_id);
      if (!out.property && rec.reference_data && rec.reference_data.unit_id) {
        const unit = units.find((u) => u.id === rec.reference_data.unit_id);
        if (unit) out.property = properties.find((p) => p.id === unit.property_id);
      }
      return out;
    });
    setReceiptList(remapped);
    if (tenantId) {
      setReceipts(remapped.filter((it) => it.tenant && it.tenant.id === tenantId));
    } else if (propertyId) {
      setReceipts(remapped.filter((it) => it.property && it.property.id === propertyId));
    } else {
      setReceipts(remapped);
    }
  }, [tenantsList, properties, units]);

  function filterByProperty() {
    if (propertyId) {
      DestroyTable('receipts_table');
      const filtered = tenantsList.filter((item) => {
        return item.unit.property_id === propertyId;
      });
      setTenants(filtered);
      const transaction = receiptList.filter((item) => {
        if (item && item.property) {
          return item.property.id === propertyId;
        }
        return undefined;
      });
      setReceipts(transaction);
    } else {
      setTenants(tenantsList);
      setReceipts(receiptList);
    }
  }

  /**
   *
   * @param {[]} property
   */
  async function loadUnits(property) {
    const propertyIds = property.map((item) => {
      return item.id;
    });
    let urlUnits;
    // get units
    if (user.type === 'admin') {
      urlUnits = `${process.env.REACT_APP_API_URL}units?take=10000`;
    } else {
      urlUnits = `${
        process.env.REACT_APP_API_URL
      }units?take=10000&where={"property_id":"in::${propertyIds.join()}"}`;
    }
    try {
      const {
        data: { response: unitsInfo },
      } = await axios.get(urlUnits, requestConfig);
      setUnits(unitsInfo);
      loadTenants(unitsInfo);
    } catch (_) {
      //
    }
  }

  /**
   *
   * @param {[]} unitsInfo
   */
  function loadTenants(unitsInfo) {
    function searchUnit(id) {
      return unitsInfo.find((item) => {
        return item.id === id;
      });
    }

    (async () => {
      try {
        const unitIds = unitsInfo.map((item) => {
          return item.id;
        });
        let urlTenants;
        // get tenats
        if (user.type === 'admin') {
          urlTenants = `${process.env.REACT_APP_API_URL}tenants?take=10000`;
        } else {
          urlTenants = `${
            process.env.REACT_APP_API_URL
          }tenants?take=10000&where={"unit_id":"in::${unitIds.join()}"}`;
        }
        const {
          data: { response },
        } = await axios.get(urlTenants, requestConfig);
        const mapped = response.map((item) => {
          const element = item;
          element.unit = searchUnit(element.unit_id);
          return element;
        });
        setTenants(mapped);
        setTenantsList(mapped);
        $(tenantRef.current)
          .select2()
          .on('change', (event) => {
            setTenantId(event.val);
          });
      } catch (_) {
        //
      }
    })();
  }

  function findTenant(id) {
    return tenantsList.find((item) => {
      return item.id === id;
    });
  }

  function findUnit(id) {
    return units.find((unit) => {
      return unit.id === id;
    });
  }

  function findProperty(id) {
    return properties.find((property) => {
      return property.id === id;
    });
  }

  async function onSearchReceipts() {
    let filter = '';
    if (tenantId) {
      filter = `"tenant_id":"${tenantId}"`;
    }
    if (status) {
      const query = filter ? ',' : '';
      filter = `${filter}${query}"status":"${status}"`;
    }
    if (!lowerDate || !upperDate) {
      // no date filter
      dispatch(AlertActions.error('Select both date filters to continue'));
      return;
    }
    if (lowerDate && upperDate) {
      // filter by date
      const query = filter ? ',' : '';
      if (lowerDate && upperDate) {
        filter = `${filter}${query}"createdat":"btwn::${DateTimeUTC(
          lowerDate,
          false,
        )},${DateTimeUTC(upperDate, true)}"`;
      }
    }
    if (!filter) {
      dispatch(AlertActions.error('Select receipts filter to continue'));
      return;
    }
    try {
      if (user.type !== 'admin') {
        const tenantsIds = tenants.map((item) => {
          return item.id;
        });
        filter = `${filter},"tenant_id":"in::${tenantsIds.join()}"`;
      }
      const urlReceipts = `${process.env.REACT_APP_API_URL}payments?where={${filter}}`;
      setIsLoading(true);
      const {
        data: { response },
      } = await axios.get(urlReceipts, requestConfig);
      mapReceipts(response);
      setIsLoading(false);
    } catch (err) {
      setError(err);
      setIsLoading(false);
    }
  }

  function mapReceipts(charges) {
    const mapped = charges.map((receipt) => {
      const element = receipt;
      element.tenant = findTenant(element.tenant_id);
      if (element.reference_data) {
        const unitId = element.reference_data.unit_id;
        const unit = findUnit(unitId);
        if (unit) {
          const propId = unit.property_id;
          const property = findProperty(propId);
          element.property = property;
        }
      }
      return element;
    });
    const sorted = SortByLatest(mapped);
    if (propertyId) {
      const filtered = sorted.filter((item) => {
        if (item && item.property) {
          return item.property.id === propertyId;
        }
        return undefined;
      });
      setReceipts(filtered);
    } else {
      setReceipts(sorted);
    }
    setIsLoading(false);
    setReceiptList(sorted);
  }

  return (
    <Master redirectLink="receipts" title="Finance" source="Receipts">
      <div className="row">
        <div className="col-md-12">
          {alert && alert.message && (
            <AlertMessage
              alertType={`alert ${alert.type}`}
              message={alert.message}
            />
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-md-9">
          <div className="row">
            <Select
              caption="Property Name"
              value={propertyId}
              onChange={(event) => {
                setPropertyId(event.target.value);
              }}
              columnWidth={6}
              inputRef={propertyRef}
            >
              <option value="">Select property name</option>
              {properties.map((item) => {
                return (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </Select>
            <Select
              caption="Tenant"
              value={tenantId}
              onChange={(event) => {
                setTenantId(event.target.value);
              }}
              columnWidth={6}
              inputRef={tenantRef}
            >
              <option value="">Select tenant</option>
              {tenants.map((item) => {
                return (
                  <option key={item.id} value={item.id}>
                    {`${item.first_name} ${item.last_name} | ${item.other_details ? item.other_details.unit_number : '--' }`}
                  </option>
                );
              })}
            </Select>
            {/* <Select
              caption="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
              }}
              columnWidth={2}
            >
              <option value="">Select status</option>
              {ReceiptStatus.map((payStatus) => {
                return (
                  <option value={payStatus.id} key={payStatus.id}>
                    {payStatus.name}
                  </option>
                );
              })}
            </Select> */}
          </div>
          <div className="row">
            <DatePicker
              placeholder="Select date from"
              caption="Date From"
              onChange={(date) => {
                return setLowerDate(date);
              }}
              columnWidth={2}
              selected={lowerDate}
            />
            <DatePicker
              placeholder="Select date to"
              caption="Date To  &nbsp; &nbsp;"
              onChange={(date) => {
                return setUpperDate(date);
              }}
              columnWidth={2}
              selected={upperDate}
            />
            <div className="col-md-3 invoice-button">
              <Button
                caption="Search"
                cssClass="btn-success"
                onClick={onSearchReceipts}
                type="button"
                isBlack={false}
              />
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="hpanel">
            <div className="panel-body">
              <span>Search Total</span>
              <br />
              <h1 className="primary-red">{`Ksh ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <Collapsible columnWidth={12} caption="Receipts" controlId="receipts">
          <ReceiptsTable
            isLoading={isLoading}
            error={error}
            receipts={receipts}
          />
        </Collapsible>
      </div>
    </Master>
  );
};

Receipts.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dispatch: PropTypes.func.isRequired,
  alert: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

Receipts.defaultProps = {
  user: {},
  alert: {},
};

const mapStateToProps = (state) => {
  return state;
};

export default connect(mapStateToProps)(Receipts);
